/** @format */

import { isSignInWithEmailLink } from "firebase/auth";
import { useCallback, useEffect, useRef, useState } from "react";
import { auth, useAuthContext } from "senf-shared";
import { useModals } from "../components/molecules/modalStack/ModalProvider";
import useDataPrivacyShield from "../components/special/auth/useDataPrivacyShield";
import AuthAddDetails from "../components/templates/auth/AuthAddDetails";
import LoggingInModal from "../components/templates/auth/LoggingInModal";
import SimpleAuthOptions from "../components/templates/auth/SimpleAuthOptions";
import { useIsMobile } from "./customDeviceDetect";

type AuthVariant = "login" | "authAddDetails" | "reAuth";
type UnknownRecord = Record<string, unknown>;

/**
 * Custom hook that handles authentication modal logic with auto-login support
 * Prevents modal from opening when auto-login conditions are met
 *
 * @example
 * ```tsx
 * const { openAuthModal, openLoggingInModal } = useAuthModal(project);
 *
 * // Open the main auth modal
 * openAuthModal("login");
 *
 * // Open signing in modal
 * openLoggingInModal("signingIn");
 *
 * // Open success modal
 * openLoggingInModal("success", { successType: "register" });
 *
 * // Open error modal with custom retry handler
 * openLoggingInModal("error", {
 *   onRetry: () => console.log("Retry clicked"),
 *   title: "Custom Error Title",
 *   description: "Custom error description"
 * });
 * ```
 */

export const useAuthModal = (project?: {
  settings?: {
    registration?: boolean;
    guestLogin?: boolean;
    demographicData?: {
      collect?: boolean;
    };
  };
  projectId?: string;
}) => {
  const { openDrawer, openModal, closeDrawer, closeModal } = useModals();
  const authContext = useAuthContext();
  const { user, signIn } = authContext;
  const { handleDataPrivacy } = useDataPrivacyShield(project);
  const [autoLoginAttempted, setAutoLoginAttempted] = useState(false);
  const isMobile = useIsMobile();

  // Reset autoLoginAttempted when user logs out
  useEffect(() => {
    if (!user) {
      setAutoLoginAttempted(false);
    }
  }, [user]);

  const handleCheckAuth = ({
    checkAuthAddDetails = true,
  }: {
    checkAuthAddDetails?: boolean;
  }) => {
    const isUserAnonymous = user?.isAnonymous;
    const isUserIdPresent = !!user?.userId;
    const isEmailNotVerified = isUserIdPresent && !user?.emailVerified;
    const isUserProfileComplete =
      user?.profileCompleted ||
      project?.settings?.demographicData?.collect === false;

    if (!user) {
      openAuthModal("login", {
        variant: "post",
        id: "authModal",
        zIndex: 9999,
      });
      return;
    }
    if (isUserIdPresent && isEmailNotVerified && !isUserAnonymous) {
      openAuthModal("login", {
        variant: "post",
        id: "authModal",
        zIndex: 9999,
      });
    }

    if (isUserIdPresent && !isUserProfileComplete && checkAuthAddDetails) {
      openAuthModal("authAddDetails", {
        id: "authModal",
        zIndex: 9999,
      });
      return;
    }
  };

  const buildAnonymousFormikStore = useCallback(() => {
    return {
      values: {
        projectId: project?.projectId || "",
        referrer:
          typeof document !== "undefined" ? document.referrer || "" : "",
        utm_id:
          typeof window !== "undefined"
            ? new URLSearchParams(window.location.search).get("utm_id") || ""
            : "",
      },
      handleChange: () => {},
      handleBlur: () => {},
    } as const;
  }, [project]);

  const openLoggingInModal = useCallback(() => {


    const modalOptions: UnknownRecord = {
      swipe: !!isMobile,
      transform: false,
    };

    const modalComponent = <LoggingInModal />;

    console.log("🔍 About to call openModal with component");
    // Always use modal for logging in states
    openModal(modalComponent as never, modalOptions as never);
  }, [openModal, isMobile]);

  const openAuthModal = useCallback(
    async (
      variant?: AuthVariant,
      additionalProps?: UnknownRecord & {
        loggingInCallback?: (updatedUser?: any) => void;
      },
    ) => {
      const isEmailAction = (() => {
        if (typeof window === "undefined") return false;
        return isSignInWithEmailLink(auth, window.location.href);
      })();

      if (isEmailAction) {
        return;
      }

      // Check if we should auto-login instead of showing modal
      // Don't auto-login for authAddDetails modal or reAuth modal
      if (
        !user &&
        !autoLoginAttempted &&
        variant !== "authAddDetails" &&
        project?.settings?.guestLogin !== false &&
        project?.settings?.registration === false
      ) {
        setAutoLoginAttempted(true);

        try {
          // Check if data privacy consent is required
          const privacyResult = handleDataPrivacy();

          if (privacyResult === true) {
            // No privacy consent required - proceed with auto-login
            const formikStore = buildAnonymousFormikStore();

            const updatedUser = await signIn({
              provider: "anonymous",
              formikStore,
            });

            return additionalProps?.loggingInCallback?.(updatedUser);
          }
          if (privacyResult instanceof Promise) {
            // Privacy consent is required - wait for user decision

            const consentGranted = await privacyResult;

            if (consentGranted) {
              // User granted consent - proceed with auto-login
              const formikStore = buildAnonymousFormikStore();

              const updatedUser = await signIn({
                provider: "anonymous",
                formikStore,
              });

              return additionalProps?.loggingInCallback?.(updatedUser);
            }
            setAutoLoginAttempted(false); // Reset so user can try again
            if (typeof additionalProps?.onClose === "function") {
              additionalProps.onClose();
            }
            return;
          }
        } catch (error) {
          console.error("Auto-login failed:", error);
          setAutoLoginAttempted(false); // Allow retry
        }
      }

      // If we reach here, either:
      // 1. Auto-login conditions not met
      // 2. Privacy consent was denied
      // 3. Auto-login failed
      // In all cases, show the normal login modal

      // Configure drawer/modal options
      const baseModalOptions: UnknownRecord = {
        swipe: !!isMobile,
        transform: isMobile ? false : true,
      };
      const { onClose, loggingInCallback, ...restAdditionalProps } =
        (additionalProps || {}) as UnknownRecord & {
          loggingInCallback?: () => void;
        };
      const modalOptions: UnknownRecord = { ...baseModalOptions, onClose };

      // Create the appropriate component directly based on variant
      let modalComponent: React.ReactNode;

      if (variant === "authAddDetails") {
        // modalOptions.size = isMobile ? "fullscreen" : "medium";
        modalComponent = (
          <div className="px-5">
            <AuthAddDetails
              user={user}
              project={project}
              onAfterSave={() => {
                if (typeof additionalProps?.onAfterSave === "function") {
                  additionalProps.onAfterSave();
                }
                // closeModal({ id: "authModal" });
                // closeDrawer({ id: "authModal" });
                // additionalProps?.onAfterSave?.();
              }}
              {...(restAdditionalProps as UnknownRecord)}
            />
          </div>
        );
      } else {
        // For login, register, reAuth - use SimpleAuthOptions
        modalComponent = (
          <SimpleAuthOptions
            project={project}
            {...(restAdditionalProps as UnknownRecord)}
            success={(user) => {
              console.log("success", user);
              additionalProps?.loggingInCallback?.(user);
            }}
          />
        );
      }

      const shouldOpenDrawer =
        !isMobile &&
        !isEmailAction &&
        additionalProps?.variant !== "infoPage" &&
        additionalProps?.variant !== "survey";

      if (shouldOpenDrawer) {
        openDrawer(modalComponent as never, modalOptions as never);
      } else {
        openModal(modalComponent as never, modalOptions as never);
      }
    },
    [
      user,
      autoLoginAttempted,
      project,
      signIn,
      handleDataPrivacy,
      openDrawer,
      openModal,
      isMobile,
      buildAnonymousFormikStore,
    ],
  );

  // Magic link detection - open modal for auto-processing
  const processingRef = useRef(false);
  const [hasProcessed, setHasProcessed] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (processingRef.current || hasProcessed) return; // Prevent multiple runs
    if (!isSignInWithEmailLink(auth, window.location.href)) return;

    processingRef.current = true;
    setHasProcessed(true);


    // Modal will auto-detect and handle the magic link
    openLoggingInModal();
  }, [openLoggingInModal, hasProcessed]);

  return { openAuthModal, openLoggingInModal, handleCheckAuth };
};
